import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowRight, CheckCircle2, Layers, Cloud, Code2,
  Database, Server, Shield, Zap, Users, Target, Briefcase,
  Box, TrendingUp, GitBranch, Settings, Lock, Rocket
} from 'lucide-react';

function LegacyModernizationPage() {
  const navigate = useNavigate();
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observers = sectionsRef.current.map((section) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        },
        { threshold: 0.1 }
      );
      if (section) observer.observe(section);
      return observer;
    });

    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  const services = [
    {
      icon: <Database className="w-8 h-8" />,
      title: "Mainframe Assessment",
      description: "Comprehensive portfolio inventory, code analysis, and business process mapping to identify modernization opportunities."
    },
    {
      icon: <Code2 className="w-8 h-8" />,
      title: "Legacy Modernization",
      description: "Transform COBOL applications to Java/.NET with automated conversion and API enablement for core systems."
    },
    {
      icon: <Cloud className="w-8 h-8" />,
      title: "Cloud Migration",
      description: "Seamless migration to AWS, Azure, or GCP with zero downtime strategy and performance validation."
    },
    {
      icon: <Layers className="w-8 h-8" />,
      title: "API Enablement",
      description: "Wrap mainframe CICS/DB2 systems with modern APIs for integration with contemporary applications."
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Managed Services",
      description: "24x7 production support, L3 expertise, and continuous optimization for modernized systems."
    },
    {
      icon: <GitBranch className="w-8 h-8" />,
      title: "DevOps Enablement",
      description: "CI/CD pipelines for z/OS builds, automated testing, and deployment automation for mainframe."
    }
  ];

  const approach = [
    {
      phase: "01",
      title: "Assessment & Discovery",
      description: "Portfolio inventory, code analysis, and business process mapping to identify modernization candidates.",
      icon: <Target className="w-6 h-6" />
    },
    {
      phase: "02",
      title: "Strategic Planning",
      description: "Define modernization roadmap with cloud alignment and comprehensive risk mitigation plans.",
      icon: <Briefcase className="w-6 h-6" />
    },
    {
      phase: "03",
      title: "Modernization Execution",
      description: "Automated code conversion, API enablement, and parallel run validation framework.",
      icon: <Rocket className="w-6 h-6" />
    },
    {
      phase: "04",
      title: "Testing & Cutover",
      description: "Automated test harness, performance validation, and zero downtime migration strategy.",
      icon: <CheckCircle2 className="w-6 h-6" />
    },
    {
      phase: "05",
      title: "Post-Modernization Support",
      description: "Knowledge transition, continuous monitoring, and system optimization.",
      icon: <Shield className="w-6 h-6" />
    }
  ];

  const strengths = [
    { icon: <Server />, text: "20+ years mainframe expertise with COBOL, JCL, DB2, CICS, VSAM" },
    { icon: <Cloud />, text: "Proven Mainframe-to-Cloud transformations (AWS, Azure)" },
    { icon: <Code2 />, text: "Expert re-platforming to Java microservices architectures" },
    { icon: <Zap />, text: "Hybrid integration specialists (Mainframe + Open systems)" },
    { icon: <Users />, text: "Scaled teams up to 80+ across development, QA, and migration" },
    { icon: <Lock />, text: "Focus on performance, compliance, and stability" }
  ];

  const technologies = {
    mainframe: ["COBOL", "JCL", "DB2", "CICS", "VSAM", "IMS", "MQ"],
    modern: ["Java", ".NET", "Microservices", "AWS", "Azure", "GCP"],
    devops: ["Jenkins", "Git", "SonarQube", "Docker", "Kubernetes"]
  };

  const engagementModels = [
    {
      title: "Fixed Price",
      description: "Well-defined scope with predictable costs and timeline",
      icon: <Box />
    },
    {
      title: "Time & Materials",
      description: "Flexible approach for evolving requirements",
      icon: <TrendingUp />
    },
    {
      title: "Staff Augmentation",
      description: "Skilled specialists integrated into your teams",
      icon: <Users />
    },
    {
      title: "Managed Delivery",
      description: "End-to-end ownership of modernization journey",
      icon: <Rocket />
    }
  ];

  return (
    <div className="legacy-modernization-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg"></div>
        <div className="hero-layout">
          <div className="hero-content">
            <div className="hero-badge">
              <Server className="w-4 h-4" />
              <span>Legacy Transformation Experts</span>
            </div>
            <h1 className="hero-title">
              Transforming Legacy Strength<br />into Modern Agility
            </h1>
            <p className="hero-subtitle">
              Evolve your mainframe core systems into modern, scalable platforms while preserving business continuity
            </p>
            <div className="hero-cta">
              <Button
                size="lg"
                className="cta-primary"
                onClick={() => navigate('/contact')}
              >
                Get Started <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="cta-secondary"
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">20+</div>
                <div className="stat-label">Years Experience</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">7M+</div>
                <div className="stat-label">Records Migrated</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">80+</div>
                <div className="stat-label">Team Members</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section" ref={(el) => (sectionsRef.current[0] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">About Us</Badge>
            <h2 className="section-title">Bridging Legacy and Innovation</h2>
            <p className="section-description">
              Legacy systems carry decades of trust – our mission is to bring them into the future without losing their strength
            </p>
          </div>

          <div className="about-grid">
            <Card className="about-card">
              <CardHeader>
                <Target className="w-10 h-10 mb-4 card-icon" />
                <CardTitle>Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  To help enterprises evolve legacy core systems into modern, scalable platforms while preserving business continuity and leveraging decades of accumulated business logic.
                </p>
              </CardContent>
            </Card>

            <Card className="about-card">
              <CardHeader>
                <Rocket className="w-10 h-10 mb-4 card-icon" />
                <CardTitle>Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  To be the trusted partner for enterprises navigating the complex journey from mainframe to cloud, delivering transformation with zero disruption.
                </p>
              </CardContent>
            </Card>

            <Card className="about-card">
              <CardHeader>
                <Users className="w-10 h-10 mb-4 card-icon" />
                <CardTitle>Leadership</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  Led by Suman Chimata with 20+ years in mainframe strategy, modernization, and delivery leadership across SAP, Java, and AI applications.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section section-alt" ref={(el) => (sectionsRef.current[1] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Services</Badge>
            <h2 className="section-title">Comprehensive Modernization Solutions</h2>
            <p className="section-description">
              End-to-end services to transform your legacy systems into modern, cloud-native applications
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <Card key={index} className="service-card">
                <CardHeader>
                  <div className="service-icon">{service.icon}</div>
                  <CardTitle className="service-title">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="service-description">{service.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="section" ref={(el) => (sectionsRef.current[2] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Our Approach</Badge>
            <h2 className="section-title">5-Phase Modernization Framework</h2>
            <p className="section-description">
              A proven methodology ensuring successful transformation with minimal risk
            </p>
          </div>

          <div className="approach-timeline">
            {approach.map((phase, index) => (
              <div key={index} className="approach-item">
                <div className="approach-number">{phase.phase}</div>
                <div className="approach-content">
                  <div className="approach-icon">{phase.icon}</div>
                  <h3 className="approach-title">{phase.title}</h3>
                  <p className="approach-description">{phase.description}</p>
                </div>
                {index < approach.length - 1 && <div className="approach-line"></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strengths Section */}
      <section className="section section-alt" ref={(el) => (sectionsRef.current[3] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Why Choose Us</Badge>
            <h2 className="section-title">Industry-Leading Expertise</h2>
          </div>

          <div className="strengths-grid">
            {strengths.map((strength, index) => (
              <div key={index} className="strength-item">
                <div className="strength-icon">{strength.icon}</div>
                <p className="strength-text">{strength.text}</p>
              </div>
            ))}
          </div>

          <Separator className="my-16" />

          <div className="tech-stack">
            <h3 className="tech-stack-title">Technology Expertise</h3>
            <div className="tech-categories">
              <div className="tech-category">
                <h4 className="tech-category-title">Mainframe</h4>
                <div className="tech-tags">
                  {technologies.mainframe.map((tech, i) => (
                    <Badge key={i} variant="secondary" className="tech-badge">{tech}</Badge>
                  ))}
                </div>
              </div>
              <div className="tech-category">
                <h4 className="tech-category-title">Modern Stack</h4>
                <div className="tech-tags">
                  {technologies.modern.map((tech, i) => (
                    <Badge key={i} variant="secondary" className="tech-badge">{tech}</Badge>
                  ))}
                </div>
              </div>
              <div className="tech-category">
                <h4 className="tech-category-title">DevOps & Tools</h4>
                <div className="tech-tags">
                  {technologies.devops.map((tech, i) => (
                    <Badge key={i} variant="secondary" className="tech-badge">{tech}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <Separator className="my-16" />

          <div className="engagement-section">
            <h3 className="engagement-title">Flexible Engagement Models</h3>
            <div className="engagement-grid">
              {engagementModels.map((model, index) => (
                <Card key={index} className="engagement-card">
                  <CardHeader>
                    <div className="engagement-icon">{model.icon}</div>
                    <CardTitle className="engagement-card-title">{model.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="engagement-card-description">{model.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section" ref={(el) => (sectionsRef.current[4] = el)}>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Transform Your Legacy Systems?</h2>
            <p className="cta-description">
              Let's discuss how our modernization solutions can help you achieve your goals
            </p>
            <Button
              size="lg"
              className="cta-primary"
              onClick={() => navigate('/contact')}
            >
              Contact Us Today <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LegacyModernizationPage;
